import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Modal from 'react-bootstrap/Modal';
import { Circles } from 'react-loader-spinner';
import base_url from './api/Config';
import TopBar from './component/TopBar';
import authFetch from './api/authFetch';

// Registration link is a hosted Razorpay page opened in the system browser —
// there's no in-page handler callback, so we poll our own status endpoint
// until the mandate reaches a final state.
const MANDATE_POLL_INTERVAL_MS = 3000;
const MANDATE_POLL_MAX_ATTEMPTS = 100; // ~5 minutes

const ProductTypes = () => {
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successModal, setSuccessModal] = useState(false);
  const [errorModal, setErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [mandatePromptModal, setMandatePromptModal] = useState(false);
  const [mandatePollingModal, setMandatePollingModal] = useState(false);
  // null when idle, otherwise the authType ('netbanking' | 'upi' | 'card') being set up
  const [mandateBusy, setMandateBusy] = useState(null);
  const [mandateMessage, setMandateMessage] = useState('');
  const [mandateSucceeded, setMandateSucceeded] = useState(false);
  const [createdLoanId, setCreatedLoanId] = useState(null);
  const mandatePollRef = useRef(null);

  useEffect(() => () => {
    if (mandatePollRef.current) clearInterval(mandatePollRef.current);
  }, []);

  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const rawAppId = params.get('applicationId');
  const applicationId = rawAppId && rawAppId !== 'null' ? rawAppId : null;
  const rawCustomerId = params.get('customerId');
  const customerId = rawCustomerId && rawCustomerId !== 'null' ? rawCustomerId : null;

  useEffect(() => {
    setIsLoading(true);
    authFetch(`${base_url}/api/product/view`)
      .then(async res => {
        if (!res.ok) throw new Error('Failed to fetch products');
        return res.json();
      })
      .then(data => {
        setProducts(Array.isArray(data) ? data : data ? [data] : []);
        setIsLoading(false);
      })
      .catch(() => {
        setError('Error fetching products. Please try again.');
        setIsLoading(false);
      });
  }, []);

  const handleProductClick = (product) => {
    if (!applicationId && !customerId) {
      setErrorMessage('Session expired. Please restart the application process.');
      setErrorModal(true);
      return;
    }
    setIsLoading(true);
    const query = applicationId
      ? `applicationId=${applicationId}&customerId=${customerId || ''}&productId=${product.id}&productName=${encodeURIComponent(product.productName)}`
      : `customerId=${customerId}&productId=${product.id}&productName=${encodeURIComponent(product.productName)}`;
    authFetch(`${base_url}/api/productLogic/createByApplicationId?${query}`, {
      method: 'POST',
      body: JSON.stringify({
        interestRate: product.interestRate,
        interestRateType: product.interestRateType,
        processFee: product.processFee,
        otherDeduction: product.otherDeduction,
        loanAmount: product.loanAmount,
        tenureInDays: product.tenureInDays,
        repaymentType: product.repaymentType,
        numberOfEmi: product.numberOfEmi,
        productId: product.id,
        productName: product.productName,
      }),
    })
      .then(async res => {
        if (!res.ok) {
          const err = await res.json();
          throw new Error(err.error || err.message || 'Error saving product.');
        }
        return res.json();
      })
      .then(data => {
        setIsLoading(false);
        setSelectedProduct(product);
        setCreatedLoanId(data?.loanId || null);
        if (data?.loanId) {
          setMandatePromptModal(true);
        } else {
          setSuccessModal(true);
        }
      })
      .catch(err => {
        setIsLoading(false);
        const msg = err.message || 'An unexpected error occurred.';
        if (msg.toLowerCase().includes('loan already exists')) {
          navigate(`/LoanList?error=${encodeURIComponent(msg)}`);
        } else {
          setErrorMessage(msg);
          setErrorModal(true);
        }
      });
  };

  const stopMandatePolling = () => {
    if (mandatePollRef.current) {
      clearInterval(mandatePollRef.current);
      mandatePollRef.current = null;
    }
  };

  // Polls our own status endpoint (backed by Razorpay's link status + webhook)
  // until the mandate reaches a final state:
  // - AUTHORIZED/CONFIRMED -> bank step is done, move on to the success screen.
  // - REJECTED/CANCELLED/FAILED -> declined or link expired, go back to the
  //   same "set up mandate?" prompt so the customer can retry or skip.
  // - still CREATED after ~5 minutes -> stop waiting and move on anyway;
  //   status can always be checked/retried later from Loan Details.
  const pollMandateStatus = (loanId) => {
    stopMandatePolling();
    let attempts = 0;
    mandatePollRef.current = setInterval(async () => {
      attempts += 1;
      try {
        const res = await authFetch(`${base_url}/api/razorpay-mandate/status/${loanId}`);
        const data = await res.json();
        const status = data?.status;
        if (status === 'AUTHORIZED' || status === 'CONFIRMED') {
          stopMandatePolling();
          setMandatePollingModal(false);
          setMandateSucceeded(true);
          setSuccessModal(true);
          return;
        }
        if (['REJECTED', 'CANCELLED', 'FAILED'].includes(status)) {
          stopMandatePolling();
          setMandatePollingModal(false);
          setMandateMessage('Bank authorisation was declined or the link expired. You can try again.');
          setMandatePromptModal(true);
          return;
        }
      } catch (e) {
        // transient network error — keep polling
      }
      if (attempts >= MANDATE_POLL_MAX_ATTEMPTS) {
        stopMandatePolling();
        setMandatePollingModal(false);
        setSuccessModal(true);
      }
    }, MANDATE_POLL_INTERVAL_MS);
  };

  // Optional auto-debit mandate setup, offered right after the loan is
  // created. Opens Razorpay's hosted registration-link page (Netbanking,
  // UPI Autopay or Card, depending on what the customer picked) in the
  // browser, then polls for the outcome — see pollMandateStatus for how
  // each outcome is handled.
  const setupMandate = async (loanId, authType) => {
    setMandateBusy(authType);
    setMandateMessage('');
    setMandateSucceeded(false);
    try {
      const createRes = await authFetch(`${base_url}/api/razorpay-mandate/create`, {
        method: 'POST',
        body: JSON.stringify({ loanId, authType }),
      });
      const data = await createRes.json();
      if (!createRes.ok || !data?.paymentLinkUrl) {
        setMandateMessage(data?.error || data?.message || 'Could not start auto-debit setup. You can try again.');
        setMandatePromptModal(true);
        return;
      }
      window.open(data.paymentLinkUrl, '_blank');
      setMandatePollingModal(true);
      pollMandateStatus(loanId);
    } catch (e) {
      setMandateMessage('Could not start auto-debit setup. You can try again.');
      setMandatePromptModal(true);
    } finally {
      setMandateBusy(null);
    }
  };

  const handleMandateYes = (authType) => {
    setMandatePromptModal(false);
    setupMandate(createdLoanId, authType);
  };

  const handleMandateNo = () => {
    setMandatePromptModal(false);
    setSuccessModal(true);
  };

  return (
    <div className="app-page">
      <TopBar title="Select Loan Product" backPath={`/VideoUpload?applicationId=${applicationId}&customerId=${customerId}`} />
      {isLoading && <div className="loader-overlay"><div className="spinner-border text-light" /></div>}

      <div className="app-content">
        {error ? (
          <div className="app-card" style={{ color: '#e53935', textAlign: 'center' }}>{error}</div>
        ) : isLoading && products.length === 0 ? (
          <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 60 }}>
            <Circles height={60} width={60} color="#1d2e77" />
          </div>
        ) : products.length === 0 ? (
          <div className="app-card" style={{ textAlign: 'center', color: '#888' }}>No products available.</div>
        ) : (
          <>
            <p style={{ fontSize: 13, color: '#666', marginBottom: 8, paddingLeft: 4 }}>
              Tap a product to apply
            </p>
            {products.map((product, i) => (
              <div
                key={i}
                className="list-card"
                onClick={() => handleProductClick(product)}
                style={{
                  background: selectedProduct?.id === product.id ? '#e8f5e9' : '#fff',
                  borderLeft: selectedProduct?.id === product.id ? '4px solid #11c232' : '4px solid transparent',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <p style={{ fontSize: 18, fontWeight: 700, color: '#1d2e77', marginBottom: 4 }}>
                      ₹{product.loanAmount?.toLocaleString()}
                    </p>
                    <p style={{ fontSize: 13, color: '#666', marginBottom: 2 }}>{product.productName}</p>
                    <p style={{ fontSize: 12, color: '#888', marginBottom: 0 }}>
                      {product.tenureInDays} days · {product.numberOfEmi} EMIs
                    </p>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <p style={{ fontSize: 13, color: '#666', marginBottom: 2 }}>
                      Interest: <strong>{product.interestRate}%</strong>
                    </p>
                    <p style={{ fontSize: 13, color: '#666', marginBottom: 0 }}>
                      Fee: <strong>₹{product.processFee}</strong>
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      <Modal show={mandatePromptModal} backdrop="static" keyboard={false} centered>
        <Modal.Body className="text-center p-4">
          <div style={{ fontSize: 40, marginBottom: 8 }}>🏦</div>
          <strong>Set up auto-debit mandate now?</strong>
          <p style={{ fontSize: 13, color: '#666', marginTop: 8 }}>
            Authorise your bank once so EMIs get auto-debited on the due date. This is optional — you can also do this later.
          </p>
          {mandateMessage && (
            <p style={{ fontSize: 12, color: '#e5393e', marginTop: 8 }}>{mandateMessage}</p>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 14 }}>
            <button className="btn btn-success" disabled={!!mandateBusy} onClick={() => handleMandateYes('netbanking')}>
              {mandateBusy === 'netbanking' ? 'Please wait…' : '🏦 Netbanking'}
            </button>
            <button className="btn btn-success" disabled={!!mandateBusy} onClick={() => handleMandateYes('upi')}>
              {mandateBusy === 'upi' ? 'Please wait…' : '📱 UPI Autopay'}
            </button>
            <button className="btn btn-success" disabled={!!mandateBusy} onClick={() => handleMandateYes('card')}>
              {mandateBusy === 'card' ? 'Please wait…' : '💳 Debit/Credit Card'}
            </button>
          </div>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-center">
          <button className="btn btn-secondary" disabled={!!mandateBusy} onClick={handleMandateNo}>
            Not now
          </button>
        </Modal.Footer>
      </Modal>

      <Modal show={mandatePollingModal} backdrop="static" keyboard={false} centered>
        <Modal.Body className="text-center p-4">
          <Circles height={40} width={40} color="#1d2e77" />
          <strong style={{ display: 'block', marginTop: 12 }}>Waiting for bank authorisation…</strong>
          <p style={{ fontSize: 13, color: '#666', marginTop: 8 }}>
            Complete the authorisation in the browser tab that just opened. This will update automatically once you're done.
          </p>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-center">
          <button
            className="btn btn-secondary"
            onClick={() => {
              stopMandatePolling();
              setMandatePollingModal(false);
              setSuccessModal(true);
            }}
          >
            Skip for now
          </button>
        </Modal.Footer>
      </Modal>

      <Modal show={successModal} onHide={() => { setSuccessModal(false); navigate('/CustomerList'); }} centered>
        <Modal.Body className="text-center p-4">
          <div style={{ fontSize: 48, marginBottom: 8 }}>✅</div>
          <strong>Loan Created Successfully!</strong>
          <p style={{ fontSize: 13, color: '#666', marginTop: 8 }}>Product selected and saved.</p>
          {mandateSucceeded && (
            <p style={{ fontSize: 13, color: '#2e9e44', fontWeight: 600, marginTop: 4 }}>
              ✅ Auto-debit mandate set up successfully!
            </p>
          )}
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-center">
          <button className="btn btn-success" onClick={() => { setSuccessModal(false); navigate('/CustomerList'); }}>
            View Customers
          </button>
        </Modal.Footer>
      </Modal>

      <Modal show={errorModal} onHide={() => setErrorModal(false)} centered>
        <Modal.Body className="text-center p-4">
          <strong>{errorMessage || 'An unexpected error occurred.'}</strong>
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-danger" onClick={() => setErrorModal(false)}>Close</button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ProductTypes;
